<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title>
            Current Inventory Balance
            <v-spacer></v-spacer>
            <v-checkbox
              v-model="hideZeroValue"
              label="Hide items with zero value"
              hide-details
              class="mt-0"
            ></v-checkbox>
          </v-card-title>
          <v-card-text>
            <v-data-table
              :headers="headers"
              :items="filteredItems"
              :loading="loading"
              :items-per-page="itemsPerPage"
              :page.sync="page"
              class="elevation-1"
              :footer-props="{
                itemsPerPageOptions: [5, 10, 25, { text: 'All', value: -1 }],
                showFirstLastPage: true,
              }"
            >
              <template v-slot:item.quantityRemaining="{ item }">
                {{ formatQuantity(item.quantityRemaining) }}
              </template>
              <template v-slot:item.currentValue="{ item }">
                {{ formatCurrency(item.currentValue) }}
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row v-if="items.length > 0" class="mt-4">
      <v-col cols="12" class="text-right">
        <v-btn color="secondary" @click="downloadCsv"> Download CSV </v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import Papa from "papaparse";

export default {
  name: "InventoryBalanceTable",
  data() {
    return {
      loading: false,
      hideZeroValue: true,
      page: 1,
      itemsPerPage: 10, // Default items per page
      headers: [
        { text: "Product ID", value: "productId", sortable: true },
        { text: "Product Name", value: "product", sortable: true },
        {
          text: "Current Weight (Quantity Remaining)",
          value: "quantityRemaining",
          sortable: true,
        },
        { text: "Current Value", value: "currentValue", sortable: true },
      ],
      items: [],
    };
  },
  computed: {
    filteredItems() {
      return this.hideZeroValue
        ? this.items.filter((item) => parseFloat(item.currentValue) !== 0)
        : this.items;
    },
  },
  async mounted() {
    await this.loadData();
  },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        const params = {
          inventoryDate: new Date().toISOString(),
          includeCost: true,
        };
        const response = await this.$axios.get("/inventory/balance", {
          params,
        });
        let rawItems = response.data.data || [];
        // Calculate quantityRemaining for each item
        const itemsWithQuantity = rawItems.map((item) => ({
          ...item,
          quantityRemaining: this.computeQuantityRemaining(item),
        }));
        // Group by productId
        this.items = this.rollupByProductId(itemsWithQuantity);
      } catch (error) {
        console.error("Error loading inventory balance:", error);
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
    computeQuantityRemaining(item) {
      const original = parseFloat(item.originalQuantity) || 0;
      const inputUsed = parseFloat(item.inputQuantityUsed) || 0;
      const sold = parseFloat(item.quantitySold) || 0;
      const deduction = parseFloat(item.adjustmentDeductionQuantity) || 0;
      const remaining = original - inputUsed - sold - deduction;
      return remaining > 0 ? remaining : 0;
    },
    rollupByProductId(items) {
      // Group items by productId
      const grouped = items.reduce((acc, item) => {
        const productId = item.productId;
        if (!acc[productId]) {
          acc[productId] = {
            productId: item.productId,
            product: item.product, // Assume product name is consistent for same productId
            quantityRemaining: 0,
            currentValue: 0,
          };
        }
        acc[productId].quantityRemaining += parseFloat(item.quantityRemaining) || 0;
        acc[productId].currentValue += parseFloat(item.currentValue) || 0;
        return acc;
      }, {});
      // Convert grouped object to array
      return Object.values(grouped).map(item => ({
        ...item,
        quantityRemaining: item.quantityRemaining.toFixed(2), // Ensure 2 decimal places
        currentValue: item.currentValue.toFixed(2), // Ensure 2 decimal places
      }));
    },
    formatQuantity(value) {
      return new Intl.NumberFormat("th-TH", {
        maximumFractionDigits: 2,
      }).format(value);
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("th-TH", {
        style: "currency",
        currency: "THB",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    },
    downloadCsv() {
      if (this.items.length === 0) return;
      const exportData = this.filteredItems.map((item) => ({
        "Product ID": item.productId,
        "Product Name": item.product,
        "Current Weight": this.formatQuantity(item.quantityRemaining),
        "Current Value": this.formatCurrency(item.currentValue),
      }));

      // Convert data to CSV using PapaParse
      const csv = Papa.unparse(exportData, {
        quotes: true,
        delimiter: ",",
        header: true,
      });

      // Create a downloadable CSV file
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "inventory_balance.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
  },
};
</script>